import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLong } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { ButtonStandard } from '../../button/ButtonStandard/ButtonStandard';
import { useScreenSm } from '../../hooks/mediaQueries/useScreenSm';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalOpened } from '../../hooks/useModalOpened/useModalOpened';
import { SelectLocationDialog } from '../../location/SelectLocationDialog/SelectLocationDialog';
import { Icon, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { InputLayout, InputLayoutProps } from '../InputLayout/InputLayout';
import sharedStyles from '../sharedStyles.module.css';
import styles from './LocationInput.module.css';

export type LocationInputProps = PropsWithoutChildren<
  Omit<
    InputLayoutProps,
    'children' | 'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture'
  >
> & {
  value: LatLong | null;
  onValueChanged: (value: LatLong | null) => void;
};

export const LocationInput: FunctionComponent<LocationInputProps> = React.memo(
  (props) => {
    const { value, onValueChanged, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { t } = useTranslation('record-it-components');

    const screenSm = useScreenSm();

    const {
      close: closeSelectLocationDialog,
      open: openSelectLocationDialog,
      modalProps: selectLocationDialogModalProps
    } = useModalOpened({ closeOnBackDropClick: false });

    const handleAcceptClick = useEventCallback((location: LatLong | null) => {
      onValueChanged(location);
      closeSelectLocationDialog();
    });

    const valueText = useMemo(() => {
      if (!value) {
        return t('input.LocationInput.noValue');
      }

      const digits = screenSm ? 6 : 2;

      return `${value.latitude.toFixed(digits)} / ${value.longitude.toFixed(digits)}`;
    }, [t, value, screenSm]);

    return (
      <>
        <InputLayout {...remainingProps}>
          {() => (
            <div className={styles.contentWrapper}>
              <input
                className={classes(sharedStyles.inputBaseStyle, styles.input)}
                value={valueText}
                size={valueText.length} // TODO: currently the input is a little bit wider than needed, but that kinda makes the padding asymetric. It's acceptable for now, but should be improved some time
                readOnly={true}
                onClick={openSelectLocationDialog}
                data-testid="LocationInput.Input"
              ></input>
              <ButtonStandard
                className={styles.selectLocationInDialogButton}
                onClick={openSelectLocationDialog}
                data-testid="LocationInput.SelectLocationButton"
              >
                <Icon
                  name="map-location-dot"
                  type={IconType.Regular}
                  className={styles.icon}
                ></Icon>
              </ButtonStandard>
            </div>
          )}
        </InputLayout>
        <SelectLocationDialog
          {...selectLocationDialogModalProps}
          location={value}
          onCancel={closeSelectLocationDialog}
          onAccept={handleAcceptClick}
          data-testid="LocationInput.SelectLocationDialog"
        ></SelectLocationDialog>
      </>
    );
  }
);
